import React, { useState } from 'react';
import { Dialog } from '@material-ui/core';
import { CoreIcon } from '@clover/web-lib-core/components';
import { useTranslation } from 'react-i18next';
import styles from './styles.module.scss';
import { CoreCheckbox } from '@clover/web-lib-core/dist/components';
import {
  FormField,
  buildQueryParamsString,
  BannerValues,
  SuggestAppFormField,
} from '../../utils/formValidationHelper';
import { SUGGEST_APP_MAX_TEXT_LENGTH, SUGGEST_APP_MIN_TEXT_LENGTH } from '../../utils/constants';
import { useAppContext } from '../../hooks/useAppContext';
import LoadingButton from '../Common/LoadingButton';
import { ButtonVariant } from '@clover/web-lib-core/dist/components/CoreButton/CoreButton';
import { SuggestAppBanner } from './suggest-app-banner';
import { RTKQueryResponseType, useSubmitAppSuggestMutation } from '../../redux/api/appMarket-api-slice';
import { SubmitSuggestAppResponse } from '../../models/post-req-responses-payloads';
import {TextFieldWithValidation} from '../Common/TextFieldWithValidation'

interface ComponentProps {
  open: boolean;
  onClose: () => void;
}

const getInitialFieldState = (value: string = ''): FormField => {
  return {
    value: value,
    error: true,
    visited: false,
  };
};
const getInitialBannerValues = (): BannerValues => {
  return {
    successBanner: false,
    errorBanner: false,
    requestFailBanner: false,
  };
};

export const SuggestAppModal = (props: ComponentProps): JSX.Element => {
  const { onClose, open } = props;
  const [submitAppSuggest, submitAppSuggestResult] = useSubmitAppSuggestMutation();
  const { isLoading: isAppSuggeestSubmitting } = submitAppSuggestResult;
  const { id: contextId, name: merchantName, email, owner } = useAppContext();
  const [checkBox, setCheckBox] = useState(true);
  const [textField, setTextField] = useState<FormField>(getInitialFieldState());
  const [showBanner, setShowBanner] = useState(false);
  const [bannerValue, setBannerValue] = useState<BannerValues>(getInitialBannerValues());
  const [t] = useTranslation();

  /* Mapping the Form Fields with respective values */
  const formEntryMap = {
    [SuggestAppFormField.MERCHANT_ID]: contextId,
    [SuggestAppFormField.MERCHANT_NAME]: merchantName,
    [SuggestAppFormField.EMAIL]: email,
    [SuggestAppFormField.OWNER]: owner,
    [SuggestAppFormField.APP_SUGGESTION]: textField.value,
    [SuggestAppFormField.CONTACT_MERCHANT]: checkBox ? 'X' : '',
  };

  const handleClose = () => {
    if (!bannerValue.errorBanner && textField.value.length > 0) {
      setShowBanner(true);
      setBannerValue({
        successBanner: false,
        errorBanner: true,
        requestFailBanner: false,
      });
    } else {
      setCheckBox(true);
      setShowBanner(false);
      setBannerValue(getInitialBannerValues());
      setTextField(getInitialFieldState());
      onClose();
    }
  };

  const toggleCheckBox = () => {
    if (checkBox === true) {
      setCheckBox(false);
    } else setCheckBox(true);
  };


  const handlePostReq = (response: RTKQueryResponseType<SubmitSuggestAppResponse>): void => {
    if ('data' in response && response.data) {
      setShowBanner(true);
      setBannerValue({
        successBanner: true,
        errorBanner: false,
        requestFailBanner: false,
      });
      setTextField(getInitialFieldState());
    } else if ('error' in response && typeof response.error === 'string') {
      setShowBanner(true);
      setBannerValue({
        successBanner: false,
        errorBanner: false,
        requestFailBanner: true,
      });
      setTextField(getInitialFieldState());
    }
  };

  const handleReviewSubmit = async () => {
    const queryParamsString = buildQueryParamsString(formEntryMap);
    let body = `version=suggestApp&values=${encodeURIComponent(queryParamsString)}`;
    const response = await submitAppSuggest({ body });
    handlePostReq(response);
  };

  const resetValues =()=>{
    setShowBanner(false)
    setBannerValue(getInitialBannerValues())
  }

  return (
    <Dialog
      onClose={handleClose}
      open={open}
      maxWidth={false}
      PaperProps={{ classes: { root: styles.suggestAppDialog } }}
    >
      <CoreIcon
        className={styles.closeIcon}
        onClick={handleClose}
        glyph="close"
        height={14}
        width={14}
        role="application"
        tabIndex={-1}
		data-testid='closeSuggestAppModal'
      />

      <div>
        {showBanner && (
          <SuggestAppBanner
            successBanner={bannerValue.successBanner}
            requestFailureBanner={bannerValue.requestFailBanner}
            notSavedBanner={bannerValue.errorBanner}
          />
        )}
        <div className={styles.dialogContent}>
          <div className={styles.titleContainer}>
            <h1 className={styles.dialogTitle}>{t('suggest_app.heading')}</h1>
            <p>{t('suggest_app.sub_heading')}</p>
          </div>
          <div className={styles.rightPanel}>
            <TextFieldWithValidation
            field={textField}
            classes={styles.textField}
            label={t('suggest_app.label')}
            executeOnChange={resetValues}
            setStateFn={setTextField}
            disabled={bannerValue.successBanner}
            maxLength={SUGGEST_APP_MAX_TEXT_LENGTH}
            minLength={SUGGEST_APP_MIN_TEXT_LENGTH}
            size='medium'
            rows={8}
            multiline={true}
            errorMessage={t('suggest_app.suggestApp_error',{number:SUGGEST_APP_MIN_TEXT_LENGTH})}
            ariaLabel={t('suggest_app.sub_heading')}
          />

            <div className={styles.bottomAction}>
              <div>
                <CoreCheckbox
                  className={styles.checkBox}
                  label={t('suggest_app.check_box_text')}
                  onChange={toggleCheckBox}
                  value="feature-selection"
                  checked={checkBox}
                />
              </div>
              <div>
                <LoadingButton
                  className={styles.submitButton}
                  isLoading={isAppSuggeestSubmitting}
                  variant={ButtonVariant.PRIMARY}
                  text={t('suggest_app.submit_button')}
                  isDisabled={bannerValue.successBanner ? bannerValue.successBanner : textField.error}
                  onClick={handleReviewSubmit}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
};
